'use client';

import {
  createContext,
  use,
  useCallback,
  useMemo,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from 'react';

/**
 * Below 1024px: slide-out drawer. 1024px and up: the icon rail.
 *
 * `'icons'` and `'full'` no longer differ anywhere. They did while the in-flow
 * sidebar could be expanded to labels at 1280px and up; that expansion is gone
 * (the rail is permanently the icon strip off the COP), and with it the only
 * `=== 'full'` comparison in the client. Every remaining consumer - `TopBar` and
 * four checks in `Sidebar` - asks `=== 'drawer'` and nothing else, so the
 * 1280px boundary below is currently a distinction that changes no behaviour.
 *
 * Left in place rather than collapsed to `'drawer' | 'rail'` because that is a
 * rename across modules and their tests, not part of removing a button. Do not
 * read the boundary as evidence that something widens at 1280px.
 */
export type DashboardBreakpointMode = 'drawer' | 'icons' | 'full';

function modeFromWidth(width: number): DashboardBreakpointMode {
  if (width < 1024) return 'drawer';
  if (width < 1280) return 'icons';
  return 'full';
}

function subscribeToResize(onStoreChange: () => void): () => void {
  window.addEventListener('resize', onStoreChange);
  return () => window.removeEventListener('resize', onStoreChange);
}

function getBreakpointModeSnapshot(): DashboardBreakpointMode {
  return modeFromWidth(window.innerWidth);
}

function getBreakpointModeServerSnapshot(): DashboardBreakpointMode {
  return 'full';
}

export type DashboardShellContextValue = {
  breakpointMode: DashboardBreakpointMode;
  drawerOpen: boolean;
  setDrawerOpen: (open: boolean) => void;
  toggleDrawer: () => void;
  /**
   * How much of the page the floating rail permanently covers, in CSS px, or 0
   * when the rail is in flow and covers nothing.
   *
   * The rail is rendered by the dashboard LAYOUT, above every route, so it sits
   * outside the COP's own `StageInsetsProvider` and cannot register with it. It
   * measures itself and publishes here; the COP reads this and forwards it as
   * the stage's left inset. Measured rather than the constant 48, because the
   * number decides where the strip board is drawn and whether there is room to
   * fly manually, and a constant goes stale the first time the rail's padding
   * changes.
   *
   * The RESTING width, never the expanded one. A rail expanding on hover is a
   * deliberate momentary action over the map; the map's chrome is laid out
   * against the strip the rail always occupies, so brushing the rail with a
   * pointer does not make the strip board dance.
   */
  railInsetPx: number;
  setRailInsetPx: (px: number) => void;
  /**
   * Whether an operator has an aircraft in hand on the COP - flying it, or
   * having asked for it and waiting on the grant.
   *
   * Published for ONE consumer: the floating rail, which must not open itself
   * while this is true. An expanded rail covers the strip board, and its nav
   * links land in the same vertical band as the aircraft rows, so a click meant
   * for a callsign navigates off the COP instead. That unmounts `CopView`, and
   * `useManualControl`'s drone-change cleanup releases manual authority on the
   * wire - the session is ended, not paused, and the aircraft goes back to the
   * RC.
   *
   * `manualExitGuard` already puts a confirmation in front of the three
   * SELECTION clicks that reach that outcome, but it reasons about
   * `SelectionAction` by design and a `<Link>` is not one. Rather than teach
   * that guard about routing, the rail stops opening itself. The toggle still
   * works: a deliberate press was never the hazard.
   *
   * The same direction of travel as `railInsetPx` in reverse - the COP knows
   * this and the rail cannot, because the rail is rendered by the layout above
   * every route and has no idea what is flying.
   */
  manualFlightEngaged: boolean;
  setManualFlightEngaged: (engaged: boolean) => void;
};

const DashboardShellContext = createContext<DashboardShellContextValue | null>(null);

export function DashboardShellProvider({ children }: { children: ReactNode }) {
  const breakpointMode = useSyncExternalStore(
    subscribeToResize,
    getBreakpointModeSnapshot,
    getBreakpointModeServerSnapshot,
  );
  const [drawerOpenRequested, setDrawerOpenRequested] = useState(false);

  const drawerOpen = breakpointMode === 'drawer' && drawerOpenRequested;

  const setDrawerOpen = useCallback((open: boolean) => {
    setDrawerOpenRequested(open);
  }, []);

  const toggleDrawer = useCallback(() => {
    setDrawerOpenRequested((o) => !o);
  }, []);

  const [railInsetPx, setRailInsetPxState] = useState(0);
  const setRailInsetPx = useCallback((px: number) => {
    // Bail on an unchanged measurement. A `ResizeObserver` fires on every layout
    // pass, and this value is read by the COP, so a state write per pass would
    // re-render the whole air picture at animation frame rate for a rail that
    // has not moved.
    setRailInsetPxState((prev) => (prev === px ? prev : px));
  }, []);

  // No bail wrapper, unlike the measurement above: this changes a handful of
  // times per sortie rather than once per layout pass, and React already skips
  // a re-render when a `useState` setter is handed the value it is holding.
  const [manualFlightEngaged, setManualFlightEngaged] = useState(false);

  const value = useMemo(
    () => ({
      breakpointMode,
      drawerOpen,
      setDrawerOpen,
      toggleDrawer,
      railInsetPx,
      setRailInsetPx,
      manualFlightEngaged,
      setManualFlightEngaged,
    }),
    [
      breakpointMode,
      drawerOpen,
      setDrawerOpen,
      toggleDrawer,
      railInsetPx,
      setRailInsetPx,
      manualFlightEngaged,
      setManualFlightEngaged,
    ],
  );

  return <DashboardShellContext.Provider value={value}>{children}</DashboardShellContext.Provider>;
}

export function useDashboardShell(): DashboardShellContextValue {
  const ctx = use(DashboardShellContext);
  if (!ctx) {
    throw new Error('useDashboardShell must be used within DashboardShellProvider');
  }
  return ctx;
}

/** TopBar and optional components: safe when rendered outside the provider. */
export function useOptionalDashboardShell(): DashboardShellContextValue | null {
  return use(DashboardShellContext);
}
